// Per-row runner payloads and the job bodies built from them (editable-plan spec Part 1a,
// "Dispatch"; contract 1.6). Pure: no React, no I/O, and no spec or meta reads. Every value a
// runner used to derive from the plan (checkpoint, properties, guidance, record trajectories,
// filters, oversample, max length, featurization model, return mode, seeds, GT4SD version and
// target) arrives here from row.params, so a row is sent exactly as it was reviewed.
import { MATTERGEN_CHECKPOINT_ROOT } from '../../components/mattergen/mattergenCheckpoints';
import { fm4mOversample } from '../../utils/generationBounds';
import { cloneJson } from './rowContract';

const nonEmpty = (o) => !!o && typeof o === 'object' && Object.keys(o).length > 0;

/** MattergenRunner.execute(payload, opts): one MatterGen row. */
export function mattergenPayloadForRow(row) {
  const params = row.params || {};
  return {
    rowId: row.id,
    checkpoint: row.target,
    custom: row.customModelId != null,
    customModelId: row.customModelId ?? null,
    dispatchTarget: row.dispatchTarget ?? null,
    properties: { ...(params.properties || {}) },
    ...(params.diffusion_guidance_factor !== undefined ? { diffusion_guidance_factor: params.diffusion_guidance_factor } : {}),
    record_trajectories: params.record_trajectories,
    candidates: row.candidates,
    chunkSize: row.chunkSize,
  };
}

/**
 * One MatterGen job body, in the key order sent; `job` comes from planMattergenJobs.
 * Decision L3: built-in, BYOC and endpoint-mode custom rows name the checkpoint as
 * model_path ROOT/<target>, so an endpoint-mode customer endpoint keeps receiving today's
 * field. A weights-mode custom row sends neither model_path nor pretrained_name: its reference
 * is the /models/<id> URL plus X-Org-Id, and llm-proxy injects checkpoint_gcs_uri. properties
 * is sent only when non-empty, and guidance only when the row has it.
 */
export function mattergenJobBody(payload, job) {
  const properties = payload.properties || {};
  return {
    ...(payload.dispatchTarget === 'custom-weights' ? {} : { model_path: `${MATTERGEN_CHECKPOINT_ROOT}/${payload.checkpoint}` }),
    batch_size: job.batch_size,
    num_batches: job.num_batches,
    ...(nonEmpty(properties) ? { properties: { ...properties } } : {}),
    ...(payload.diffusion_guidance_factor !== undefined ? { diffusion_guidance_factor: payload.diffusion_guidance_factor } : {}),
    record_trajectories: payload.record_trajectories,
  };
}

function featurizeOf(row, model) {
  const params = row.params || {};
  return {
    model,
    return_mode: params.return_mode,
    ...(params.return_mode === 'inline' && params.max_items_inline !== undefined ? { max_items_inline: params.max_items_inline } : {}),
    chunkSize: row.chunkSize,
  };
}

/** FM4MRunner.execute(payload, opts): one FM4M row, generate or represent. */
export function fm4mPayloadForRow(row) {
  const params = row.params || {};
  if (row.kind === 'represent') {
    return {
      rowId: row.id,
      kind: 'represent',
      domain: row.domain,
      customModelId: null,
      seeds: [...(params.seed_smiles || [])],
      featurize: featurizeOf(row, row.target),
    };
  }
  return {
    rowId: row.id,
    kind: 'generate',
    domain: row.domain,
    generator: row.target,
    customModelId: row.customModelId ?? null,
    candidates: row.candidates,
    generation: {
      filters: cloneJson(params.filters || {}),
      // The number the service runs with and the per-run tally charges (R4-2): 1 whenever
      // filters are empty, whatever oversample_multiplier says.
      oversample_multiplier: fm4mOversample(params),
      max_length: params.max_length,
    },
    featurize: featurizeOf(row, params.featurization_model),
  };
}

/** One FM4M generation job body (POST /jobs). Every field is explicit; filters may be {}. */
export function fm4mGenerationJobBody(payload, numSamples) {
  const generation = payload.generation || {};
  return {
    model: payload.generator,
    num_samples: numSamples,
    oversample_multiplier: generation.oversample_multiplier,
    max_length: generation.max_length,
    filters: generation.filters || {},
  };
}

/** One FM4M featurization chunk body (POST /jobs/representation); `model` is the chain entry tried. */
export function fm4mFeaturizeBody(featurize, model, inputs) {
  return {
    model,
    inputs,
    return_mode: featurize.return_mode,
    ...(featurize.return_mode === 'inline' && Number.isFinite(featurize.max_items_inline) ? { max_items_inline: featurize.max_items_inline } : {}),
  };
}

/** GT4SDRunner.execute(payload, opts): one GT4SD row. */
export function gt4sdPayloadForRow(row) {
  const params = row.params || {};
  return {
    rowId: row.id,
    customModelId: row.customModelId ?? null,
    algorithm: row.target,
    algorithm_version: params.algorithm_version,
    ...(params.target_smiles !== undefined ? { target_smiles: params.target_smiles } : {}),
    ...(params.target_property !== undefined ? { target_property: params.target_property } : {}),
    ...(params.target_value !== undefined ? { target_value: params.target_value } : {}),
    candidates: row.candidates,
  };
}

/** One GT4SD job body (POST /generate_job), in the key order sent. */
export function gt4sdJobBody(payload, nSamples) {
  return {
    algorithm: payload.algorithm,
    n_samples: nSamples,
    algorithm_version: payload.algorithm_version,
    ...(payload.target_smiles !== undefined ? { target_smiles: payload.target_smiles } : {}),
    ...(payload.target_property !== undefined ? { target_property: payload.target_property } : {}),
    ...(payload.target_value !== undefined ? { target_value: payload.target_value } : {}),
  };
}
